import { useUserPreferencesState } from '@/hooks/useUserPreferencesState';

import {
  DEFAULT_SERIES_SELECTION,
  EMPTY_SERIES_SELECTION,
  MAX_REMEMBERED_TARGETS,
  SELECTION_PREFERENCES_KEY,
} from '../config/seriesConfig';
import type { SeriesSelection, SeriesSelectionByTargetId } from '../types/SeriesSelection.types';
import {
  changeSelectedSeries,
  deselectSeries,
  rememberSelection,
  toggleHighlightedSeries,
} from '../utils/seriesSelection';

/**
 * Owner of the per-target series selection: local state extended with user-scoped persistence, so
 * a target reopens with the series the user last looked at. Without a target there is no selection.
 */
export function useSeriesSelectionPreferences(targetId: number | undefined) {
  const [selectionByTargetId, setSelectionByTargetId] = useUserPreferencesState<SeriesSelectionByTargetId>(
    SELECTION_PREFERENCES_KEY,
    {}
  );

  const selection =
    targetId === undefined ? EMPTY_SERIES_SELECTION : (selectionByTargetId[targetId] ?? DEFAULT_SERIES_SELECTION);

  function updateSelection(update: (current: SeriesSelection) => SeriesSelection) {
    if (targetId === undefined) {
      return;
    }

    // Read outside the updater so the updater stays pure.
    const updatedAt = Date.now();

    setSelectionByTargetId((current) =>
      rememberSelection({
        selections: current,
        targetId,
        selection: update(current[targetId] ?? DEFAULT_SERIES_SELECTION),
        updatedAt,
        maxRememberedTargets: MAX_REMEMBERED_TARGETS,
      })
    );
  }

  return {
    selectedSeriesIds: selection.selectedSeriesIds,
    highlightedSeriesIds: selection.highlightedSeriesIds,
    changeSelectedSeries: (seriesIds: string[]) =>
      updateSelection((current) => changeSelectedSeries(current, seriesIds)),
    deselectSeries: (seriesId: string) => updateSelection((current) => deselectSeries(current, seriesId)),
    toggleHighlight: (seriesId: string) => updateSelection((current) => toggleHighlightedSeries(current, seriesId)),
  };
}
